import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, Box, Typography, Button, Chip, CircularProgress, Alert } from '@mui/material';
import { Close as CloseIcon, Warning as WarningIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { useIncident } from '../../contexts/IncidentContext';
import { useAuth } from '../../contexts/AuthContext';
import IncidentActions from './IncidentActions';
import IncidentTimeline from './IncidentTimeline';
import IncidentResolveButton from './IncidentResolveButton';

const INCIDENT_THEMES = {
  lockdown: {
    color: '#d32f2f',
    bgColor: '#ffebee',
    label: 'LOCKDOWN'
  },
  medical: {
    color: '#1976d2',
    bgColor: '#e3f2fd',
    label: 'MEDICAL EMERGENCY'
  },
  panic_portable: {
    color: '#d32f2f',
    bgColor: '#ffebee',
    label: 'PANIC ALERT'
  },
  fire: {
    color: '#f57c00',
    bgColor: '#fff3e0',
    label: 'FIRE EMERGENCY'
  },
  weather: {
    color: '#0288d1',
    bgColor: '#e1f5fe',
    label: 'WEATHER EMERGENCY'
  },
  security: {
    color: '#7b1fa2',
    bgColor: '#f3e5f5',
    label: 'SECURITY INCIDENT'
  },
  evacuation: {
    color: '#f57c00',
    bgColor: '#fff3e0',
    label: 'EVACUATION'
  },
  other: {
    color: '#616161',
    bgColor: '#f5f5f5',
    label: 'INCIDENT'
  }
};

const IncidentModal = () => {
  const { activeIncident, modalOpen, connectionLost, userRoles, siteMap } = useIncident();
  const { userData } = useAuth();
  const [elapsedTime, setElapsedTime] = useState('');

  // Calculate elapsed time
  useEffect(() => {
    if (!activeIncident?.createdAt) return;

    const updateTimer = () => {
      const now = new Date();
      const created = activeIncident.createdAt?.toDate ? activeIncident.createdAt.toDate() : new Date(activeIncident.createdAt);
      const diff = Math.floor((now - created) / 1000);

      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;

      if (hours > 0) {
        setElapsedTime(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setElapsedTime(`${minutes}m ${seconds}s`);
      } else {
        setElapsedTime(`${seconds}s`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [activeIncident]);

  // Set Electron window always on top
  useEffect(() => {
    if (modalOpen && window.electron?.setAlwaysOnTop) {
      window.electron.setAlwaysOnTop(true);
      console.log('[INCIDENT] Set window always on top');
    }

    return () => {
      if (window.electron?.setAlwaysOnTop) {
        window.electron.setAlwaysOnTop(false);
        console.log('[INCIDENT] Removed always on top');
      }
    };
  }, [modalOpen]);

  if (!activeIncident) return null;

  const theme = INCIDENT_THEMES[activeIncident.type] || INCIDENT_THEMES.other;
  const cannotClose = activeIncident.status === 'active';

  const handleClose = (event, reason) => {
    // Prevent closing if incident is active
    if (cannotClose) {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        return;
      }
    }
  };

  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={cannotClose}
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxHeight: '90vh',
          borderTop: `4px solid ${theme.color}`,
          zIndex: 9999
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: theme.bgColor,
          borderBottom: `2px solid ${theme.color}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <WarningIcon sx={{ fontSize: 32, color: theme.color }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.color, mb: 0.5 }}>
              {theme.label}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {activeIncident.siteId && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Site: {siteMap[activeIncident.siteId] || activeIncident.siteId}
                </Typography>
              )}
              {activeIncident.createdBy?.email && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  By: {activeIncident.createdBy.email}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {elapsedTime}
              </Typography>
            </Box>
          </Box>
          <Chip
            label="ACTIVE"
            sx={{
              bgcolor: theme.color,
              color: 'white',
              fontWeight: 700,
              fontSize: '0.875rem',
              px: 1
            }}
          />
        </Box>
        {!cannotClose && (
          <IconButton
            onClick={() => handleClose(null, 'closeButton')}
            sx={{ ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {/* Connection Lost Banner */}
      {connectionLost && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Connection Lost - Incident data may be outdated
          </Typography>
        </Alert>
      )}

      {/* Content */}
      <DialogContent sx={{ p: 3 }}>
        {activeIncident.title && (
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            {activeIncident.title}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 3, minHeight: 400 }}>
          {/* Left Column - Actions */}
          <Box sx={{ flex: 1, borderRight: '1px solid', borderColor: 'divider', pr: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Actions
            </Typography>
            <IncidentActions
              incident={activeIncident}
              userRoles={userRoles}
              theme={theme}
            />
          </Box>

          {/* Right Column - Timeline */}
          <Box sx={{ flex: 1, pl: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Timeline
            </Typography>
            <IncidentTimeline
              incidentId={activeIncident.incidentId}
              tenantId={activeIncident.tenantId}
            />
          </Box>
        </Box>
      </DialogContent>

      {/* Footer - Resolve Button */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <IncidentResolveButton
          incident={activeIncident}
          userRoles={userRoles}
          theme={theme}
        />
      </Box>
    </Dialog>
  );
};

export default IncidentModal;
